import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './ToiletSpinWheel.css';

// ═══════════════════════════════════════════════════════════════════════════
// TOILET SPINWHEEL - EXCLUSIVE PREMIUM FEATURE
// Golden toilet-themed prize wheel for Toilet Mode holders
// ═══════════════════════════════════════════════════════════════════════════

const PRIZES = [
  { id: 'legendary', name: 'LEGENDARY', value: 100, bonus: '+100 $DISTIL', color: '#FFD700', chance: 5,  icon: '👑' },
  { id: 'epic',      name: 'EPIC',      value: 50,  bonus: '+50 $DISTIL',  color: '#9333EA', chance: 10, icon: '💜' },
  { id: 'rare',      name: 'RARE',      value: 25,  bonus: '+25 $DISTIL',  color: '#3B82F6', chance: 15, icon: '💎' },
  { id: 'common',    name: 'COMMON',    value: 10,  bonus: '+10 $DISTIL',  color: '#10B981', chance: 25, icon: '💚' },
  { id: 'streak',    name: 'STREAK BOOST', value: 3, bonus: '+3 Flush Streak', color: '#F59E0B', chance: 20, icon: '🔥' },
  { id: 'gas',       name: 'GAS SAVER', value: 0.5, bonus: '50% Gas Discount', color: '#06B6D4', chance: 15, icon: '⛽' },
  { id: 'badge',     name: 'BADGE',     value: 0,   bonus: 'Wheel Winner Badge', color: '#EC4899', chance: 10, icon: '🏆' },
];

const WHEEL_STORAGE_KEY = 'distil_toilet_wheel';

// ─── PROPS: onPrizeClaimed added, isWalletConnected for demo mode ───
function ToiletSpinWheel({ isOpen, onClose, flushCount, onPrizeWon, onPrizeClaimed, isWalletConnected = false }) {
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [selectedPrize, setSelectedPrize] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [spinsAvailable, setSpinsAvailable] = useState(0);
  const [dailySpinUsed, setDailySpinUsed] = useState(false);
  const [bonusSpinUsed, setBonusSpinUsed] = useState(false);
  const [nextResetTime, setNextResetTime] = useState(null);
  const [timeUntilReset, setTimeUntilReset] = useState('');

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowResult(false);
      setSelectedPrize(null);
      setSpinning(false);
    }
  }, [isOpen]);

  // Helper: get today's date string
  const getTodayKey = () => new Date().toISOString().slice(0, 10);

  // Helper: ms until midnight
  const getMsUntilMidnight = () => {
    const now = new Date();
    const midnight = new Date(now);
    midnight.setHours(24, 0, 0, 0);
    return midnight.getTime() - now.getTime();
  };

  // Check spin availability
  useEffect(() => {
    if (!isOpen) return;

    // Demo mode: always 2 spins, no tracking
    if (!isWalletConnected) {
      setSpinsAvailable(2);
      setDailySpinUsed(false);
      setBonusSpinUsed(false);
      setNextResetTime(null);
      return;
    }

    const today = getTodayKey();
    const wheelData = JSON.parse(localStorage.getItem(WHEEL_STORAGE_KEY) || '{}');

    // Reset if new day
    const isToday = wheelData.date === today;
    const dailyUsed = isToday ? (wheelData.dailySpinUsed || false) : false;
    const bonusUsed = isToday ? (wheelData.bonusSpinUsed || false) : false;

    setDailySpinUsed(dailyUsed);
    setBonusSpinUsed(bonusUsed);

    const hasBonusSpin = flushCount >= 10 && !bonusUsed;
    const hasDailySpin = !dailyUsed;
    const total = (hasDailySpin ? 1 : 0) + (hasBonusSpin ? 1 : 0);
    setSpinsAvailable(total);

    if (total === 0) {
      const midnight = new Date();
      midnight.setHours(24, 0, 0, 0);
      setNextResetTime(midnight.getTime());
    } else {
      setNextResetTime(null);
    }
  }, [isOpen, flushCount, isWalletConnected]);

  // Countdown to midnight reset
  useEffect(() => {
    if (!nextResetTime) return;

    const updateTimer = () => {
      const remaining = nextResetTime - Date.now();
      if (remaining <= 0) {
        setTimeUntilReset('');
        setNextResetTime(null);
        setSpinsAvailable(flushCount >= 10 ? 2 : 1);
        return;
      }
      const hours = Math.floor(remaining / (1000 * 60 * 60));
      const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
      setTimeUntilReset(`${hours}h ${minutes}m`);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 60000);
    return () => clearInterval(interval);
  }, [nextResetTime, flushCount]);

  // Select prize based on weighted probability
  const selectPrize = () => {
    const rand = Math.random() * 100;
    let cumulative = 0;
    for (const prize of PRIZES) {
      cumulative += prize.chance;
      if (rand <= cumulative) return prize;
    }
    return PRIZES[3]; // Fallback to common
  };

  const handleSpin = () => {
    if (spinsAvailable <= 0 || spinning) return;

    setSpinning(true);
    setShowResult(false);

    const prize = selectPrize();
    setSelectedPrize(prize);

    const prizeIndex = PRIZES.findIndex(p => p.id === prize.id);
    const segmentAngle = 360 / PRIZES.length;
    const targetRotation = -(prizeIndex * segmentAngle + segmentAngle / 2);
    const extraSpins = 3 + Math.floor(Math.random() * 3);
    const finalRotation = (extraSpins * 360) + targetRotation;

    setRotation(finalRotation);

    setTimeout(() => {
      setSpinning(false);
      setShowResult(true);

      if (isWalletConnected) {
        // Consume daily spin first, then bonus
        const today = getTodayKey();
        const wheelData = JSON.parse(localStorage.getItem(WHEEL_STORAGE_KEY) || '{}');
        const isToday = wheelData.date === today;
        const currentDailyUsed = isToday ? (wheelData.dailySpinUsed || false) : false;
        const currentBonusUsed = isToday ? (wheelData.bonusSpinUsed || false) : false;

        let newDailyUsed = currentDailyUsed;
        let newBonusUsed = currentBonusUsed;

        if (!currentDailyUsed) {
          newDailyUsed = true;
        } else if (!currentBonusUsed && flushCount >= 10) {
          newBonusUsed = true;
        }

        const updated = { date: today, dailySpinUsed: newDailyUsed, bonusSpinUsed: newBonusUsed };
        localStorage.setItem(WHEEL_STORAGE_KEY, JSON.stringify(updated));
        setDailySpinUsed(newDailyUsed);
        setBonusSpinUsed(newBonusUsed);

        const hasBonusLeft = flushCount >= 10 && !newBonusUsed;
        const newTotal = (!newDailyUsed ? 1 : 0) + (hasBonusLeft ? 1 : 0);
        setSpinsAvailable(newTotal);
        if (newTotal === 0) {
          const midnight = new Date(); midnight.setHours(24, 0, 0, 0);
          setNextResetTime(midnight.getTime());
        }

        if (onPrizeWon) onPrizeWon(prize);
      }
      // Demo mode: no tracking, just show result
    }, 4000);
  };

  const handleClaim = () => {
    onClose();

    setTimeout(() => {
      setSelectedPrize(null);
      setShowResult(false);
    }, 100);

    // ✅ BUG FIX: onPrizeClaimed is now properly in props — fires correctly
    if (isWalletConnected && onPrizeClaimed) {
      onPrizeClaimed();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="toilet-wheel-overlay" onClick={onClose}>
        <motion.div
          className="toilet-wheel-modal"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          transition={{ type: 'spring', damping: 25 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="toilet-wheel-header">
            <div className="toilet-wheel-icon">🚽</div>
            <h2 className="toilet-wheel-title">TOILET PRIZE WHEEL</h2>
            <p className="toilet-wheel-subtitle">Exclusive for Toilet Mode VIPs</p>
          </div>

          {/* Demo mode notice */}
          {!isWalletConnected && (
            <div className="demo-notice">
              👁️ Demo mode — connect wallet to earn real prizes
            </div>
          )}

          {/* Wheel rules */}
          <div className="wheel-rules">
            <span>🎰 1 free daily spin</span>
            <span>•</span>
            <span>🧹 Bonus spin at 10 purifications</span>
          </div>

          {/* Wheel Container */}
          <div className="toilet-wheel-container">
            <div className="wheel-pointer">▼</div>

            <motion.div
              className="toilet-wheel"
              animate={{ rotate: rotation }}
              transition={{
                duration: spinning ? 4 : 0,
                ease: spinning ? [0.25, 0.1, 0.25, 1] : 'linear',
              }}
            >
              <svg width="300" height="300" viewBox="0 0 300 300" className="wheel-svg">
                <defs>
                  {PRIZES.map((prize) => (
                    <linearGradient key={`grad-${prize.id}`} id={`grad-${prize.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: prize.color, stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: prize.color, stopOpacity: 0.8 }} />
                    </linearGradient>
                  ))}
                </defs>
                {PRIZES.map((prize, index) => {
                  const segmentAngle = 360 / PRIZES.length;
                  const startAngle = index * segmentAngle - 90;
                  const endAngle = startAngle + segmentAngle;
                  const startRad = (startAngle * Math.PI) / 180;
                  const endRad = (endAngle * Math.PI) / 180;
                  const x1 = 150 + 140 * Math.cos(startRad);
                  const y1 = 150 + 140 * Math.sin(startRad);
                  const x2 = 150 + 140 * Math.cos(endRad);
                  const y2 = 150 + 140 * Math.sin(endRad);
                  const largeArc = segmentAngle > 180 ? 1 : 0;
                  return (
                    <g key={prize.id}>
                      <path
                        d={`M 150 150 L ${x1} ${y1} A 140 140 0 ${largeArc} 1 ${x2} ${y2} Z`}
                        fill={`url(#grad-${prize.id})`}
                        stroke="rgba(0,0,0,0.3)"
                        strokeWidth="2"
                      />
                      <text
                        x={150 + 100 * Math.cos((startRad + endRad) / 2)}
                        y={150 + 100 * Math.sin((startRad + endRad) / 2)}
                        fontSize="24"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="white"
                        fontWeight="bold"
                        style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.8))' }}
                      >
                        {prize.icon}
                      </text>
                    </g>
                  );
                })}
              </svg>
              <div className="wheel-center">
                <div className="wheel-center-icon">🚽</div>
              </div>
            </motion.div>
          </div>

          {/* Status / Result */}
          {!showResult ? (
            <div className="toilet-wheel-status">
              {isWalletConnected ? (
                spinsAvailable > 0 ? (
                  <>
                    <p className="status-text">
                      {spinsAvailable === 2 ? '🎰 2 spins available!' : '🎰 1 spin available!'}
                    </p>
                    {flushCount < 10 && !dailySpinUsed && (
                      <p className="status-subtext">🧹 Purify 10 objects for a bonus spin ({flushCount}/10)</p>
                    )}
                    {flushCount >= 10 && !bonusSpinUsed && (
                      <p className="status-subtext">✅ Daily + bonus spin ready!</p>
                    )}
                    {flushCount < 10 && dailySpinUsed && (
                      <p className="status-subtext">🧹 Purify {10 - flushCount} more for bonus spin</p>
                    )}
                  </>
                ) : (
                  <>
                    <p className="status-text">⏰ Spins reset in</p>
                    <p className="status-timer">{timeUntilReset}</p>
                    <p className="status-subtext">Daily spin + bonus resets at midnight</p>
                  </>
                )
              ) : (
                <>
                  <p className="status-text">🎰 Try your luck!</p>
                  <p className="status-subtext">Connect wallet to win real prizes</p>
                </>
              )}
            </div>
          ) : (
            <motion.div
              className="toilet-wheel-result"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', damping: 20 }}
            >
              <div className="result-icon" style={{ color: selectedPrize?.color }}>
                {selectedPrize?.icon}
              </div>
              <h3 className="result-title">{selectedPrize?.name}!</h3>
              <p className="result-bonus">{selectedPrize?.bonus}</p>
              {isWalletConnected ? (
                <p className="result-description">Prize will be applied to your next flush!</p>
              ) : (
                <p className="result-description result-description-demo">
                  🔗 Connect wallet to claim real prizes!
                </p>
              )}
            </motion.div>
          )}

          {/* Buttons */}
          <div className="toilet-wheel-buttons">
            {!showResult ? (
              <>
                <button className="wheel-btn wheel-btn-secondary" onClick={onClose}>
                  Close
                </button>
                <button
                  className="wheel-btn wheel-btn-primary"
                  onClick={handleSpin}
                  disabled={isWalletConnected ? (spinsAvailable <= 0 || spinning) : spinning}
                >
                  {spinning ? '🎰 SPINNING...' : (isWalletConnected && spinsAvailable === 2) ? '🎰 SPIN! (2 left)' : '🎰 SPIN NOW!'}
                </button>
              </>
            ) : (
              <button className="wheel-btn wheel-btn-claim" onClick={handleClaim}>
                {isWalletConnected ? '✅ Claim Prize!' : '✅ Close'}
              </button>
            )}
          </div>

          {/* Prize List */}
          <div className="toilet-wheel-prizes">
            <p className="prizes-title">Prize Pool:</p>
            <div className="prizes-grid">
              {PRIZES.map(prize => (
                <div key={prize.id} className="prize-item">
                  <span className="prize-icon">{prize.icon}</span>
                  <span className="prize-name">{prize.bonus}</span>
                  <span className="prize-chance">{prize.chance}%</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

export default ToiletSpinWheel;
